import React, { useRef, useMemo } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { useIntl } from '../../../../util/reactIntl';
import { isMainSearchTypeKeywords } from '../../../../util/search';
import { Form, LocationAutocompleteInput } from '../../../../components';
import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const identity = v => v;

const KeywordSearchField = props => {
  const { keywordSearchWrapperClasses, iconClass, intl, isMobile = false, inputRef } = props;
  return (
    <div className={keywordSearchWrapperClasses}>
      <button className={css.searchSubmit}>
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>
      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <input
              className={isMobile ? css.mobileInput : css.desktopInput}
              {...input}
              id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              ref={inputRef}
              type="text"
              placeholder={intl.formatMessage({
                id: 'TopbarSearchForm.placeholder',
              })}
              autoComplete="off"
            />
          );
        }}
      />
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        // Merge the standard onChange function with custom behaviur. A better solution would
        // be to use the FormSpy component from Final Form and pass onChange to the
        // onChange prop but that breaks due to insufficient subscription handling.
        // See: https://github.com/final-form/react-final-form/issues/159
        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
          />
        );
      }}
    />
  );
};

/**
 * The main search form for the Topbar.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.desktopInputRoot root class for desktop form input
 * @param {Function} props.onSubmit
 * @param {boolean} props.isMobile
 * @param {Object} props.appConfig
 * @returns {JSX.Element} search form element
 */
const TopbarSearchForm = props => {
  const searchInpuRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const { appConfig, onSubmit, ...restOfProps } = props;

  // Read current pub_type from URL
  const currentType = useMemo(() => {
    const params = new URLSearchParams(history.location.search || '');
    return params.get('pub_type') || ''; // '' => placeholder selected
  }, [history.location.search]);

  const onChange = location => {
    if (!isMainSearchTypeKeywords(appConfig) && location.selectedPlace) {
      onSubmit({ location });
      searchInpuRef?.current?.blur();
    }
  };

  const onKeywordSubmit = values => {
    if (isMainSearchTypeKeywords(appConfig)) {
      onSubmit({ keywords: values.keywords, searchType: values?.searchType });
      searchInpuRef?.current?.blur();
    }
  };

  const isKeywordsSearch = isMainSearchTypeKeywords(appConfig);
  const submit = isKeywordsSearch ? onKeywordSubmit : onSubmit;

  return (
    <FinalForm
      {...restOfProps}
      key={currentType} // re-init when URL changes
      initialValues={{ searchType: currentType }} // '' shows placeholder
      onSubmit={submit}
      render={({ rootClassName, className, desktopInputRoot, isMobile = false, handleSubmit }) => {
        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;
        const preventFormSubmit = e => e.preventDefault();
        const submitFormFn = isKeywordsSearch ? handleSubmit : preventFormSubmit;

        return (
          <Form className={classes} onSubmit={submitFormFn} enforcePagePreloadFor="SearchPage">
            <div className={css.searchContainer}>
              {isKeywordsSearch ? (
                <KeywordSearchField
                  keywordSearchWrapperClasses={css.keywordSearchWrapper}
                  iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInpuRef}
                />
              ) : (
                <LocationSearchField
                  desktopInputRootClass={desktopInputRootClass}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInpuRef}
                  onLocationChange={onChange}
                />
              )}
            </div>
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
